import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import {
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Badge,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
} from 'reactstrap';
import classnames from 'classnames';
import Swal from 'sweetalert2';
import useProcurementDetail from '../../../../../../shared/offer-domain/procurement/useProcurementDetail';
import useSubmittedBidder from '../../../../hooks/useSubmittedBidder';
import { RESPOND_LIST_PAGE_SIZE } from '../../../../../../shared/offer-domain/respond/listParams';
import useClarificationProgressActions from '../../../../hooks/useClarificationProgressActions';
import { displayValue } from '../../../../utils/tenderEvaluationHelpers';
import {
  CLARIFICATION_STATUS,
  getClarificationStatusBadge,
  isTeClarificationEditable,
  isTeClarificationLocked,
} from '../../../../../../shared/offer-domain/clarification';
import { SubmitConfirmModal } from '../../../../../../shared/offer-domain/ui';
import {
  getVisibleProcurementClauses,
  useMarkClarificationRespondsAsRead,
} from '../../../../../../shared/offer-domain/respond';
import OfferingTab from './tabs/OfferingTab';
import DocumentAttachmentTab from './tabs/DocumentAttachmentTab';

const MENU_TABS = [
  {
    id: 'offering',
    label: 'Offering',
    icon: 'ri-store-2-fill',
  },
  {
    id: 'document-attachment',
    label: 'Document Attachment',
    icon: 'ri-file-text-line',
  },
];

const CONFIRM_COPY = {
  closeClarification: {
    title: 'Closing Clarification',
    message:
      'Close this clarification. Tender Evaluation and Improvement Clarification will be marked as Closing.',
  },
  sendClarification: {
    title: 'Send Clarification',
    message:
      'Send clarification to DMUT. Status will move to Waiting Response from Dmut.',
  },
};

const ClarificationDetail = ({
  procurementId: procurementIdProp,
  offerId: offerIdProp,
  procurement: procurementProp,
  isLoadingProcurement,
} = {}) => {
  const params = useParams();
  const procurementId = procurementIdProp ?? params.id;
  const offerId = offerIdProp ?? params.offerId;
  const navigate = useNavigate();
  const hasSharedProcurement = isLoadingProcurement !== undefined;
  const { procurement: fetchedProcurement, isLoading: isLoadingFetched } =
    useProcurementDetail(procurementId, { enabled: !hasSharedProcurement });
  const procurement = hasSharedProcurement
    ? procurementProp
    : fetchedProcurement;
  const isLoading = hasSharedProcurement
    ? isLoadingProcurement
    : isLoadingFetched;
  const {
    items: submittedBidders,
    isLoading: isLoadingSubmittedBidder,
    refresh,
  } = useSubmittedBidder(procurementId, { pageSize: RESPOND_LIST_PAGE_SIZE });
  const [activeTab, setActiveTab] = useState('offering');
  const [confirmAction, setConfirmAction] = useState(null);
  const [localStatus, setLocalStatus] = useState(null);
  const [highlightArmed, setHighlightArmed] = useState(false);
  const [focusRespondId, setFocusRespondId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const pendingBySourceRef = useRef({ offering: [], document: [] });
  const hasRespondBySourceRef = useRef({ offering: false, document: false });
  const [pendingReady, setPendingReady] = useState({
    offering: false,
    document: false,
  });
  const [livePendingTargets, setLivePendingTargets] = useState([]);
  const [hasClarificationRespond, setHasClarificationRespond] = useState(false);
  const { savingAction, isSaving, sendClarification, closeClarification } =
    useClarificationProgressActions({
      idOffer: offerId,
      procurementId,
    });

  useEffect(() => () => toast.dismiss(), []);

  useEffect(() => {
    setHighlightArmed(false);
    setFocusRespondId(null);
    setFocusToken(0);
    pendingBySourceRef.current = { offering: [], document: [] };
    hasRespondBySourceRef.current = { offering: false, document: false };
    setPendingReady({ offering: false, document: false });
    setLivePendingTargets([]);
    setHasClarificationRespond(false);
  }, [offerId]);

  useEffect(() => {
    if (!procurementId) return;
    refresh();
  }, [procurementId, offerId, refresh]);

  const handlePendingFrom = useCallback((source, targets, meta = {}) => {
    pendingBySourceRef.current[source] = targets ?? [];
    if (typeof meta.hasClarificationRespond === 'boolean') {
      hasRespondBySourceRef.current[source] = meta.hasClarificationRespond;
      setHasClarificationRespond(
        Boolean(hasRespondBySourceRef.current.offering) ||
          Boolean(hasRespondBySourceRef.current.document)
      );
    }
    setPendingReady(prev =>
      prev[source] ? prev : { ...prev, [source]: true }
    );
    const next = [
      ...(pendingBySourceRef.current.offering ?? []),
      ...(pendingBySourceRef.current.document ?? []),
    ];
    setLivePendingTargets(prev => {
      if (
        prev.length === next.length &&
        prev.every((item, index) => item.id === next[index]?.id)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleOfferingPending = useCallback(
    targets => handlePendingFrom('offering', targets),
    [handlePendingFrom]
  );
  const handleDocumentPending = useCallback(
    targets => handlePendingFrom('document', targets),
    [handlePendingFrom]
  );

  const isPendingReady = pendingReady.offering && pendingReady.document;

  const matchedBidder = submittedBidders.find(
    bidder =>
      String(bidder.idOffer ?? '') === String(offerId ?? '') ||
      String(bidder.id ?? '') === String(offerId ?? '')
  );
  const procurementMitraId = matchedBidder?.id ?? null;
  const clauseDetailIds = useMemo(
    () =>
      getVisibleProcurementClauses(procurement)
        .map(clause => clause.id)
        .filter(Boolean),
    [procurement]
  );

  useMarkClarificationRespondsAsRead({
    procurementId,
    procurementMitraId,
    clauseDetailIds,
    isEvaluatorView: true,
    enabled: Boolean(procurementId && procurementMitraId),
  });

  const statusClarification =
    localStatus !== null ? localStatus : matchedBidder?.statusClarification;
  const statusBadge = getClarificationStatusBadge(
    statusClarification,
    matchedBidder?.statusClarificationLabel
  );
  const isEditable = isTeClarificationEditable(statusClarification);
  const isRespondClosed = isTeClarificationLocked(statusClarification);

  useEffect(() => {
    if (!focusRespondId) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(`ic-respond-${focusRespondId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [focusRespondId, activeTab, focusToken]);

  const handleOpenSend = () => {
    if (!isEditable) return;
    if (!isPendingReady) return;
    if (livePendingTargets.length) {
      const first = livePendingTargets[0];
      setHighlightArmed(true);
      setActiveTab(first.tab);
      setFocusRespondId(first.id);
      setFocusToken(token => token + 1);
      Swal.fire({
        icon: 'error',
        title: 'Action required',
        text: 'Last counter responses must be agreed or responded to before sending clarification.',
        confirmButtonColor: '#f1556c',
      });
      return;
    }
    if (!hasClarificationRespond) {
      Swal.fire({
        icon: 'info',
        title: 'Respond required',
        text: 'Please send a respond first before Send Clarification. If no further clarification is needed, use Closing Clarification instead.',
        confirmButtonColor: '#556ee6',
      });
      return;
    }
    setHighlightArmed(false);
    setFocusRespondId(null);
    setConfirmAction('sendClarification');
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    let success = false;
    if (action === 'closeClarification') {
      success = await closeClarification();
      if (success) {
        setLocalStatus(CLARIFICATION_STATUS.CLOSING);
      }
    } else if (action === 'sendClarification') {
      success = await sendClarification(statusClarification);
      if (success) {
        setLocalStatus(CLARIFICATION_STATUS.WAITING_DMUT);
      }
    }
    if (success) {
      setConfirmAction(null);
      await refresh();
      setLocalStatus(null);
    }
  };

  const backUrl = `/apps-tenderevaluation/dtl/${procurementId}`;
  const procurementNumber = displayValue(procurement?.procurementNumber);
  const offerDisplay =
    isLoadingSubmittedBidder && !matchedBidder
      ? '…'
      : displayValue(matchedBidder?.idOffer);

  document.title = 'Clarification Detail';

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} />
      <div className='page-content'>
        <Container fluid>
          <div className='email-wrapper d-lg-flex gap-1 mx-n4 mt-n4 p-1'>
            <div className='email-content minimal-border'>
              <div className='p-4 pb-0'>
                <div className='border-bottom border-bottom-dashed'>
                  <Row className='mt-n2 mb-1 mb-sm-0'>
                    <div className='col-sm order-3 order-sm-2'>
                      <div className='hstack gap-sm-1 align-items-center flex-wrap email-topbar-link'>
                        <h5 className='card-title mb-0 flex-grow-1'>
                          <Breadcrumb listClassName='p-3 py-2 bg-light'>
                            <BreadcrumbItem>
                              <Link to='/dashboard'>Home</Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                              <Link to='/apps-tenderevaluation/view'>
                                Tender Eval
                              </Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem>
                              <Link to={backUrl}>
                                Detail #{procurementNumber}
                              </Link>
                            </BreadcrumbItem>
                            <BreadcrumbItem>Clarification</BreadcrumbItem>
                            <BreadcrumbItem active>
                              {offerDisplay}
                            </BreadcrumbItem>
                          </Breadcrumb>
                        </h5>
                      </div>
                    </div>
                  </Row>

                  <Row className='mb-4'>
                    <Col xs={12}>
                      <Row className='align-items-center g-3'>
                        <Col xs={12} lg>
                          <div className='d-flex align-items-start gap-3'>
                            <Button
                              color='light'
                              className='border shadow-sm rounded-circle d-flex align-items-center justify-content-center p-0'
                              style={{ width: '2.5rem', height: '2.5rem' }}
                              onClick={() => navigate(backUrl)}
                            >
                              <i className='bx bx-arrow-back fs-5 text-dark'></i>
                            </Button>
                            <div className='flex-grow-1'>
                              <div className='d-flex flex-wrap align-items-center gap-2 mb-1'>
                                <h4 className='mb-0 fw-bold text-dark'>
                                  Clarification — Offer {offerDisplay}
                                </h4>
                              </div>
                              <div className='d-flex flex-wrap align-items-center gap-2 text-muted small'>
                                <i className='bx bx-hash'></i>
                                <span>Procurement ID: {procurementNumber}</span>
                                <Badge
                                  color={statusBadge.color}
                                  className='px-2 py-1 fs-11 fw-medium rounded-pill'
                                >
                                  Status: {statusBadge.label}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>

                <div className='message-list-content mx-n4 px-4 message-list-scroll'>
                  <Card>
                    <CardBody className='checkout-tab'>
                      <Nav
                        tabs
                        className='nav-tabs-custom nav-primary mt-1 mb-3'
                      >
                        {MENU_TABS.map(tab => (
                          <NavItem key={tab.id}>
                            <NavLink
                              className={classnames('cursor-pointer', {
                                active: activeTab === tab.id,
                              })}
                              onClick={() => setActiveTab(tab.id)}
                            >
                              <i
                                className={`${tab.icon} me-1 align-bottom`}
                              ></i>
                              <span className='fw-semibold'>{tab.label}</span>
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>

                      <TabContent activeTab={activeTab}>
                        <TabPane tabId='offering'>
                          {isLoading && !procurement ? (
                            <div className='text-center py-4'>
                              <Spinner size='sm' color='primary' />
                              <p className='text-muted mt-2 mb-0 fs-13'>
                                Loading procurement...
                              </p>
                            </div>
                          ) : (
                            <OfferingTab
                              procurement={procurement}
                              procurementId={procurementId}
                              offerId={offerId}
                              procurementMitraId={procurementMitraId}
                              isRespondClosed={isRespondClosed}
                              highlightArmed={highlightArmed}
                              onPendingRespondIds={handleOfferingPending}
                            />
                          )}
                        </TabPane>
                        <TabPane tabId='document-attachment'>
                          <DocumentAttachmentTab
                            procurementId={procurementId}
                            offerId={offerId}
                            procurementMitraId={procurementMitraId}
                            isRespondClosed={isRespondClosed}
                            highlightArmed={highlightArmed}
                            onPendingRespondIds={handleDocumentPending}
                          />
                        </TabPane>
                      </TabContent>
                    </CardBody>
                  </Card>

                  {isEditable && (
                    <div className='req-actionbar'>
                      <Button
                        color='danger'
                        type='button'
                        className='d-flex align-items-center gap-2'
                        disabled={isSaving}
                        onClick={() => setConfirmAction('closeClarification')}
                      >
                        {savingAction === 'closeClarification' ? (
                          <Spinner size='sm' />
                        ) : (
                          <i className='ri-checkbox-circle-line'></i>
                        )}
                        <span>Closing Clarification</span>
                      </Button>
                      <Button
                        color='primary'
                        type='button'
                        className='d-flex align-items-center gap-2'
                        disabled={isSaving || !isPendingReady}
                        onClick={handleOpenSend}
                      >
                        {savingAction === 'sendClarification' ? (
                          <Spinner size='sm' />
                        ) : (
                          <i className='bx bx-send'></i>
                        )}
                        <span>Send Clarification</span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

      <SubmitConfirmModal
        isOpen={confirmAction === 'closeClarification'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={CONFIRM_COPY.closeClarification.title}
        message={CONFIRM_COPY.closeClarification.message}
        cancelLabel='Back'
        confirmLabel='Close'
        isSubmitting={savingAction === 'closeClarification'}
      />
      <SubmitConfirmModal
        isOpen={confirmAction === 'sendClarification'}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirm}
        title={CONFIRM_COPY.sendClarification.title}
        message={CONFIRM_COPY.sendClarification.message}
        cancelLabel='Back'
        confirmLabel='Send'
        isSubmitting={savingAction === 'sendClarification'}
      />
    </React.Fragment>
  );
};

export default ClarificationDetail;
